import { InjectionToken, Provider } from '@angular/core';

export const CORE_API_ROUTES = new InjectionToken('core.module.api-routes');

const apiRoutes = {
  auth: {
    login: '/authorization/token',
    refreshToken: '/auth/refresh-token',
    me: '/auth/me',
    logout: '/auth/logout',
    sendResetPasswordLink: '/auth/sendResetPasswordLink',
    verifyResetToken: '/auth/verifyResetToken',
    verifyApprovalToken: token => `/approvals/token/${token}?action=verify`,
    resetPassword: '/auth/resetPassword',
  },
  user: {
    users: '/users',
    id: userId => `/users/${userId}`,
    updateUserActiveStatus: (userId, action) => `/users/${userId}?action=${action}`,
    permissionId: permissionId => `/users/permissionId/${permissionId}`,
  },
  permission: {
    permissions: '/permissions',
    permission: '/permission',
    id: permissionId => `/permission/${permissionId}`,
  },
  role: {
    roles: '/roles',
    role: '/role',
    id: roleId => `/role/${roleId}`,
  },
  customer: {
    customers: '/customers',
    search: '/customers/search',
    exportCustomer: '/customers/export',
    info: customerId => `/customer/${customerId}/info`,
    currentRelease: customerId => `/customer/${customerId}/info/current-release`,
    repository: customerId => `/customer/${customerId}/repository`,
    repositories: customerId => `/customer/${customerId}/repositories`,
    exportOptions: (customerId, branchId, repo2BranchId, repo3BranchId) =>
      `/customer/${customerId}/repositories/export/options?branchId=${branchId}${
        repo2BranchId ? '&repo2BranchId=' + repo2BranchId : ''
      }${repo3BranchId ? '&repo3BranchId=' + repo3BranchId : ''}`,
    logs: customerId => `/customer/${customerId}/logs`,
    legacyDeployments: customerId => `/customer/${customerId}/deployments/legacy`,
    mainstreamDeployments: customerId => `/customer/${customerId}/deployments/mainstream`,
    releases: customerId => `/approvals/custom/${customerId}`,
    githubReleases: customerId => `/customer/${customerId}/repo-releases`,
    deploymentLogs: deploymentId => `/customer/deployment/${deploymentId}/logs`,
    sync: customerId => `/customer/${customerId}/sync`,
  },
  email: {
    getEmailSettings: '/email/settings',
    setEmailSettings: '/email/settings',
    testEmailSettings: '/email/test',
  },
  repo: {
    commonRepoBranch: '/repos/common-repo/branches',
    syncCommonRepoRelease: '/repos/common-repo?action=sync',
  },
  ie: {
    allVersions: '/ie/versions',
    version: version => `/ie/versions/${version}/installation-version`,
  },
  repoBranch: {
    ieVersionsByID: branchId => `/repo-branches/${branchId}/ie-versions`,
    releasesByID: branchId => `/repo-branches/${branchId}/release-summary`,
  },
  repoRelease: {
    summaryByReleaseId: releaseId => `/repo-releases/common/${releaseId}/customers/summary`,
    customersByReleaseId: releaseId => `/repo-releases/common/${releaseId}/customers`,
    repoReleaseById: releaseId => `/repo-releases/${releaseId}`,
    releaseNotesById: releaseId => `/repo-releases/${releaseId}/release-notes`,
    fwDependency: releaseId => `/repo-releases/${releaseId}/framework-release`,
  },
  frameworkRelease: {
    releases: '/framework-releases',
    qualifiedReleasesByName: version => `/framework-releases/versions/${version}/`,
    byId: id => `/framework-releases/${id}`,
    getURLbyId: id => `/framework-releases/${id}/download-link`,
    notesById: id => `/framework-releases/${id}/release-notes`,
    internalReleaseNotes: id => `/framework-releases/${id}/release-notes/internal`,
    uploadFile: `/framework-releases/file`,
    customersSummary: releaseId => `/framework-releases/${releaseId}/customers/summary`,
    customers: releaseId => `/framework-releases/${releaseId}/customers`,
  },
  publish: {
    publish: requireEncoding => (requireEncoding ? '/patches?requireEncoding=true' : '/patches'),
    offlineDownload: '/patches/offline',
    downloadCommonRepoBySha: sha => `/patches/common-repo/sha/${sha}`,
    downloadCommonRepoByVersion: version => `/patches/common-repo/versions/${version}/`,
    publishPFPInternal: '/patches/tests/platform',
    publishFWPInternal: '/patches/tests/framework',
  },
  approvals: {
    framework: '/approvals/framework',
    common: '/approvals/common',
    fetchReleaseReports: '/approvals/custom',
    exportReleaseReports: '/approvals/custom/exports',
    customized: customerId => `/approvals/custom/${customerId}`,
    taskId: taskId => `/approvals/${taskId}`,
    decision: (taskId, decision) => `/approvals/${taskId}?action=${decision}`,
  },
};

export const coreApiRoutesProvider: Provider = {
  provide: CORE_API_ROUTES,
  useValue: apiRoutes,
};
